import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clubService, eventService } from '../services/apiClient';
import Navbar from '../components/Navbar';
import CreateEventModal from '../components/CreateEventModal';
import ImageGallery from '../components/ImageGallery';
import { AuthContext } from '../context/AuthContext';
import { formatCountdown, formatDate } from '../utils/dateFormatters';

const ClubDetailsPage = () => {
  const { user } = React.useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);

  useEffect(() => {
    fetchClubDetails();
    fetchEvents();
  }, [id]);

  const fetchClubDetails = async () => {
    try {
      const response = await clubService.getClubById(id);
      // Backend returns club directly
      setClub(response.data);
    } catch (error) {
      console.error('Failed to fetch club:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await eventService.getEventsByClub(id);
      // Handle the case where response.data.events might exist or it's just response.data
      const allEvents = Array.isArray(response.data) ? response.data : (response.data.events || []);
      const now = new Date();

      setEvents(allEvents.filter(e => new Date(e.date) > now && e.status !== 'Completed'));
      setPastEvents(allEvents.filter(e => new Date(e.date) <= now || e.status === 'Completed'));
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClub = () => navigate(`/membership?club=${id}`);
  const handleAskDoubt = () => navigate(`/forum?club=${id}`);

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (!club) return <div className="text-center py-8">Club not found</div>;

  const isAdmin = user?.role === 'SuperAdmin' ||
    club.admin === user?._id ||
    club.admin?._id === user?._id;

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="h-48 bg-gradient-to-r from-purple-700 to-indigo-800 relative">
            <div className="absolute -bottom-10 left-8">
              <div className="w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center text-4xl font-bold text-purple-600 border-4 border-white">
                {club.name.charAt(0)}
              </div>
            </div>
          </div>
          <div className="pt-12 pb-8 px-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900">{club.name}</h1>
                <p className="text-purple-600 font-medium">{club.category}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button onClick={handleJoinClub} className="bg-purple-600 text-white px-8 py-2.5 rounded-lg font-bold hover:bg-purple-700 transition shadow-md">
                  Join Club
                </button>
                <button onClick={handleAskDoubt} className="border-2 border-purple-600 text-purple-600 px-6 py-2 rounded-lg font-bold hover:bg-purple-50 transition">
                  Ask Doubt
                </button>
                {isAdmin && (
                  <button onClick={() => setShowEventModal(true)} className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-green-700 transition flex items-center gap-2">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Event
                  </button>
                )}
              </div>
            </div>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl">{club.description}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section */}
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm">💡</span>
                About
              </h2>
              <div className="space-y-6">
                {club.vision && (
                  <div>
                    <h3 className="text-sm uppercase tracking-wider text-gray-500 font-bold mb-2">Vision</h3>
                    <p className="text-gray-700 leading-relaxed italic border-l-4 border-purple-200 pl-4">{club.vision}</p>
                  </div>
                )}
                {club.mission && (
                  <div>
                    <h3 className="text-sm uppercase tracking-wider text-gray-500 font-bold mb-2">Mission</h3>
                    <p className="text-gray-700 leading-relaxed">{club.mission}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                  {club.objectives?.length > 0 && (
                    <div>
                      <h3 className="text-sm uppercase tracking-wider text-gray-500 font-bold mb-3">Objectives</h3>
                      <ul className="space-y-2">
                        {club.objectives.map((obj, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-600">
                            <span className="mt-1.5 w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0"></span>
                            {obj}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {club.majorActivities?.length > 0 && (
                    <div>
                      <h3 className="text-sm uppercase tracking-wider text-gray-500 font-bold mb-3">Major Activities</h3>
                      <ul className="space-y-2">
                        {club.majorActivities.map((act, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-600">
                            <span className="mt-1.5 w-1.5 h-1.5 bg-indigo-500 rounded-full flex-shrink-0"></span>
                            {act}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {club.achievements?.length > 0 && (
                  <div className="pt-4">
                    <h3 className="text-sm uppercase tracking-wider text-gray-500 font-bold mb-3">🏆 Achievements</h3>
                    <div className="flex flex-wrap gap-2">
                      {club.achievements.map((ach, i) => (
                        <span key={i} className="bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium border border-yellow-200">
                          {ach}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Event Sections */}
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg items-center justify-center text-sm hidden sm:flex">📅</span>
                Upcoming Events
              </h2>
              {events.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {events.map((event) => (
                    <div key={event._id} className="border border-gray-100 p-5 rounded-xl hover:shadow-md transition bg-gray-50">
                      <h3 className="font-bold text-gray-800 mb-2 truncate">{event.name}</h3>
                      <div className="text-sm text-gray-600 space-y-1 mb-4">
                        <p className="flex items-center gap-2"><span>📍</span> {event.venue}</p>
                        <p className="flex items-center gap-2"><span>📅</span> {formatDate(event.date)}</p>
                      </div>
                      <div className="bg-indigo-600 text-white text-center py-1.5 rounded-lg text-xs font-bold mb-3">
                        {formatCountdown(event.date)}
                      </div>
                      <button onClick={() => navigate(`/event/${event._id}`)} className="w-full py-2 text-purple-600 font-bold text-sm hover:bg-purple-50 rounded-lg transition border border-purple-100">
                        Register Now
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic pb-4">No upcoming events scheduled.</p>
              )}

              {pastEvents.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Past Events Highlights</h3>
                  <div className="space-y-8">
                    {pastEvents.map((event) => (
                      <div key={event._id} className="border-t border-gray-100 pt-6 last:mb-0">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="font-bold text-gray-800">{event.name}</h4>
                            <p className="text-sm text-gray-500">{formatDate(event.date)}</p>
                          </div>
                        </div>
                        {event.image && (
                          <div className="mb-4">
                            <ImageGallery images={[event.image]} />
                          </div>
                        )}
                        <p className="text-sm text-gray-600 mb-4">{event.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Leadership Section */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Leadership</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Core Committee</h3>
                  <div className="space-y-3">
                    {[
                      { role: 'Chair', person: club.leadership?.chair },
                      { role: 'Vice Chair', person: club.leadership?.viceChair },
                      { role: 'Secretary', person: club.leadership?.secretary },
                      { role: 'Treasurer', person: club.leadership?.treasurer }
                    ].map((lead, i) => lead.person && (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg translate-all hover:translate-x-1">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center font-bold text-purple-600 text-sm flex-shrink-0">
                          {lead.person.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">{lead.role}</p>
                          <p className="text-sm font-bold text-gray-800">{lead.person.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {club.leadership?.xcom?.length > 0 && (
                  <div>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">XCom (Executive Committee)</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {club.leadership.xcom.map((member, i) => (member && member.name) && (
                        <div key={i} className="flex items-center gap-2 p-2 bg-indigo-50 rounded-lg">
                          <div className="w-6 h-6 bg-indigo-200 rounded-full flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0">
                            {member.name.charAt(0)}
                          </div>
                          <p className="text-[11px] font-bold text-indigo-900 truncate">{member.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Stats Summary */}
            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 p-6 rounded-xl shadow-md text-white">
              <h3 className="font-bold mb-4 opacity-80 uppercase text-xs tracking-widest">At a Glance</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-white border-opacity-20 pb-2">
                  <span className="text-sm opacity-90">Total Members</span>
                  <span className="text-xl font-bold">{club.memberCount || 0}</span>
                </div>
                <div className="flex justify-between items-center border-b border-white border-opacity-20 pb-2">
                  <span className="text-sm opacity-90">Upcoming</span>
                  <span className="text-xl font-bold">{events.length}</span>
                </div>
                <div className="flex justify-between items-center pb-2">
                  <span className="text-sm opacity-90">Completed</span>
                  <span className="text-xl font-bold">{pastEvents.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEventModal && (
        <CreateEventModal
          clubId={id}
          onClose={() => setShowEventModal(false)}
          onSuccess={fetchEvents}
        />
      )}
    </div>
  );
};

export default ClubDetailsPage;
